import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { loginUser, logoutUser, getCurrentUser, isAuthenticated, isEnabledRoleFlag } from '../services/authService';
import { getApiMessage } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getCurrentUser);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      const userData = await loginUser(username, password);
      setUser(userData);
      return userData;
    } catch (err) {
      const message = getApiMessage(err, 'Login failed. Please try again.');
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    logoutUser();
    setUser(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(() => {
    const isSuperAdmin = isEnabledRoleFlag(user?.is_super_admin);
    const isAdmin = isEnabledRoleFlag(user?.is_admin) || isSuperAdmin;

    return {
      user,
      loading,
      error,
      login,
      logout,
      clearError,
      isLoggedIn: isAuthenticated() && !!user,
      isAdmin,
      isSuperAdmin,
      roleLabel: isSuperAdmin ? 'Super Admin' : (isAdmin ? 'Admin' : 'Employee'),
    };
  }, [user, loading, error, login, logout, clearError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
